import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, Brain, Heart, Crown, Gift, Check, Sun } from "lucide-react";
import Footer from "../components/Footer";

const HERO = "https://images.pexels.com/photos/1024960/pexels-photo-1024960.jpeg?auto=compress&cs=tinysrgb&w=1100";
const PROFILE_1 = "https://images.pexels.com/photos/8629727/pexels-photo-8629727.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const CHAT_PREVIEW = "https://images.pexels.com/photos/4694510/pexels-photo-4694510.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const Landing = () => {
  return (
    <div className="min-h-screen relative" data-testid="landing-page">
      {/* Soft glow halos for warmth */}
      <div aria-hidden className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[40rem] h-[40rem] rounded-full"
             style={{background: "radial-gradient(closest-side, rgba(232,184,111,0.35), transparent 70%)"}} />
        <div className="absolute top-[40vh] -left-40 w-[36rem] h-[36rem] rounded-full"
             style={{background: "radial-gradient(closest-side, rgba(245,217,194,0.45), transparent 70%)"}} />
      </div>

      {/* Top nav */}
      <header className="alv-header fixed top-0 inset-x-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <Heart className="w-5 h-5" style={{color: "var(--alv-terracotta)"}} fill="currentColor" />
            <span className="font-display text-xl alv-text">Soulmatch</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm alv-text-soft hover:text-[var(--alv-terracotta)] transition-colors px-4 py-2" data-testid="landing-login">
              Entrar
            </Link>
            <Link to="/register" className="btn-alv-primary text-sm" data-testid="landing-register">
              Criar conta
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-16">
        <div className="relative max-w-6xl mx-auto px-6 md:px-12 py-12 md:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-12 items-center">
            <div className="animate-fade-in-up relative">
              <span className="alv-label inline-flex items-center gap-2">
                <Sun className="w-3.5 h-3.5" /> Encontros com profundidade
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light mt-4 leading-[1.05] alv-heading">
                O amor começa<br />no carácter,<br />
                <span className="font-accent italic alv-accent">não na fotografia.</span>
              </h1>
              <p className="mt-6 text-base md:text-lg alv-text-soft max-w-xl leading-relaxed">
                Soulmatch é uma plataforma de encontros amorosos guiada por personalidade.
                Avaliamos os teus traços, valores, crenças e preferências para sugerir ligações
                verdadeiramente significativas — em Portugal e no mundo.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/register" className="btn-alv-primary" data-testid="hero-cta-register">
                  Começar avaliação
                </Link>
                <Link to="/login" className="btn-alv-secondary" data-testid="hero-cta-login">
                  Já tenho conta
                </Link>
              </div>
            </div>

            {/* Hero photo */}
            <div className="hidden lg:block relative w-full max-w-[22rem] mx-auto rounded-3xl overflow-hidden alv-card"
                 style={{aspectRatio: "4 / 5", transform: "rotate(2deg)"}}>
              <img src={HERO} alt="" className="w-full h-full object-cover"
                   style={{filter: "saturate(1.1) brightness(1.05)"}} />
              <div className="absolute inset-0"
                   style={{background: "linear-gradient(180deg, rgba(255,255,255,0) 60%, rgba(250,243,231,0.4) 100%)"}} />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="text-center max-w-2xl mx-auto mb-12">
          <span className="alv-label">Porquê Soulmatch</span>
          <h2 className="font-display text-3xl md:text-4xl mt-4 alv-heading">
            Três pilares para uma <span className="font-accent italic alv-accent">ligação real</span>.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Brain,       title: "Análise multi-dimensional", desc: "Avaliamos os Cinco Grandes traços, valores fundamentais, crenças e preferências sociais." },
            { icon: Sparkles,    title: "Conselheiro com IA",        desc: "Cada ligação sugerida vem com análise de afinidade, pontos fortes e perguntas para iniciar conversa." },
            { icon: ShieldCheck, title: "Discreto e seguro",         desc: "Sem fotografias intrusivas, sem swipes infinitos. Apenas conexões pensadas para durar." },
          ].map(({icon: Icon, title, desc}, i) => (
            <div key={title} className="alv-card p-8" data-testid={`feature-${i}`}>
              <div className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                   style={{background: "linear-gradient(135deg, rgba(232,184,111,0.25), rgba(209,122,71,0.18))"}}>
                <Icon className="w-5 h-5" style={{color: "var(--alv-terracotta)"}} />
              </div>
              <h3 className="font-display text-2xl mb-3 alv-heading">{title}</h3>
              <p className="alv-text-muted leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 gap-12 items-center">
        <div className="order-2 md:order-1">
          <span className="alv-label">Como funciona</span>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight alv-heading">
            Um <span className="font-accent italic alv-accent">questionário editorial</span>, não um inquérito.
          </h2>
          <p className="mt-6 alv-text-soft leading-relaxed">
            Em poucos minutos respondes a perguntas pensadas para revelar o teu carácter.
            O nosso algoritmo cruza personalidade, valores e estilo de vida para apresentar
            apenas pessoas com afinidade real. Cada ligação inclui um índice de compatibilidade
            e três perguntas para começar a conversa.
          </p>
          <div className="mt-8 flex gap-4">
            <Link to="/register" className="btn-alv-primary" data-testid="showcase-cta">Quero experimentar</Link>
          </div>
        </div>

        <div className="order-1 md:order-2 grid grid-cols-2 gap-4">
          <div className="rounded-3xl overflow-hidden aspect-[3/4] alv-card p-0">
            <img src={PROFILE_1} alt="" className="w-full h-full object-cover"
                 style={{filter: "saturate(1.08) brightness(1.04)"}} />
          </div>
          <div className="rounded-3xl overflow-hidden aspect-[3/4] mt-8 alv-card p-0">
            <img src={CHAT_PREVIEW} alt="" className="w-full h-full object-cover"
                 style={{filter: "saturate(1.08) brightness(1.04)"}} />
          </div>
        </div>
      </section>

      {/* Pricing model */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-20" data-testid="pricing-model">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <span className="alv-label">Planos</span>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-tight alv-heading">
            Grátis para começar.<br />
            <span className="font-accent italic alv-accent">Premium quando fizer sentido.</span>
          </h2>
          <p className="mt-6 alv-text-soft leading-relaxed">
            Não acreditamos em barreiras antes de mostrarmos valor. Damos-te ferramentas reais
            para descobrires com profundidade — só pedimos para subscreveres quando quiseres
            ir muito além.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Free */}
          <article className="alv-card p-8 flex flex-col">
            <span className="alv-label" style={{color: "var(--alv-muted)"}}>Conta Soulmatch</span>
            <div className="mt-3 mb-2 font-display text-4xl alv-heading">Grátis</div>
            <p className="text-sm alv-text-muted mb-6">para sempre.</p>
            <ul className="space-y-2.5 text-sm alv-text-soft flex-1">
              {["Questionário de 45 perguntas", "Discover ilimitado por afinidade", "Score de compatibilidade em cada perfil", "5 likes por dia", "Chat com ligações mútuas", "Diário de encontros privado"].map((t) => (
                <li key={t} className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{color: "var(--alv-terracotta)"}} /> {t}
                </li>
              ))}
            </ul>
          </article>

          {/* Tokens (highlighted) */}
          <article className="alv-card-glow p-8 flex flex-col relative">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium tracking-wide text-white"
                 style={{background: "linear-gradient(135deg, var(--alv-terracotta), var(--alv-gold))"}}>
              Incluído na conta grátis
            </div>
            <span className="alv-label inline-flex items-center gap-1.5">
              <Gift className="w-3.5 h-3.5" /> Fichas de descoberta
            </span>
            <div className="mt-3 mb-2 font-display text-4xl alv-accent">3 + bónus</div>
            <p className="text-sm alv-text-muted mb-6">grátis no início.</p>
            <p className="text-sm alv-text-soft leading-relaxed mb-5">
              Cada ficha gasta num perfil específico desbloqueia <strong className="alv-text">para sempre</strong>:
            </p>
            <ul className="space-y-2.5 text-sm alv-text-soft flex-1">
              {["Análise IA completa do par", "Perguntas para o 1º encontro", "Verificação pública (RGPD)"].map((t) => (
                <li key={t} className="flex gap-2">
                  <Sparkles className="w-4 h-4 mt-0.5 shrink-0 alv-accent" /> {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 text-xs alv-text-muted space-y-1.5"
                 style={{borderTop: "1px solid rgba(197,168,128,0.22)"}}>
              <p className="inline-flex items-center gap-1.5"><span className="alv-accent font-medium">+1</span> ao completares o teu perfil</p>
              <p className="inline-flex items-center gap-1.5"><span className="alv-accent font-medium">+1</span> na tua primeira ligação mútua</p>
            </div>
          </article>

          {/* Premium */}
          <article className="alv-card p-8 flex flex-col">
            <span className="alv-label inline-flex items-center gap-1.5" style={{color: "var(--alv-muted)"}}>
              <Crown className="w-3.5 h-3.5" /> Premium
            </span>
            <div className="mt-3 mb-1 font-display text-4xl alv-heading">€7,99<span className="text-base alv-text-muted">/mês</span></div>
            <p className="text-sm alv-text-muted mb-6">ou €59,99/ano · cancela quando quiseres.</p>
            <ul className="space-y-2.5 text-sm alv-text-soft flex-1">
              {["Tudo do plano grátis", "Likes ilimitados", "Análise IA em todos os perfis", "Quem te deu like (sem precisar de match)", "Quem visitou o teu perfil", "Filtros avançados de afinidade/distância", "Re-gerar análise IA com novos ângulos"].map((t) => (
                <li key={t} className="flex gap-2">
                  <Check className="w-4 h-4 mt-0.5 shrink-0" style={{color: "var(--alv-terracotta)"}} /> {t}
                </li>
              ))}
            </ul>
          </article>
        </div>

        <p className="text-center text-sm alv-text-muted mt-10 max-w-2xl mx-auto">
          Subscrever Premium faz sentido quando queres olhar com profundidade para
          <strong className="alv-text"> mais de 5 pessoas</strong>, ou quando queres ferramentas
          horizontais como <em>ver quem te deu like sem ter de fazer match</em>. As fichas são para
          começar consciente; o Premium é para abraçar a busca a sério.
        </p>
        <div className="mt-8 flex justify-center">
          <Link to="/register" className="btn-alv-primary" data-testid="pricing-cta">Criar conta grátis</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
